import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';

import Form from '../../Atoms/Form';
import Button from '../../Atoms/Button';
import Input from '../../Atoms/Input';
import Text from '../../Atoms/Text';

import './index.sass';

const ERROR_MESSAGE_IDS = {
	COUPON_NOT_FOUND: 'couponNotFound',
	COUPON_ALREADY_USED: 'couponAlreadyUsed',
	COUPON_ALREADY_ADDED: 'couponAlreadyAdded',
	COUPON_NOT_ALLOWED: 'couponNotAllowed',
	ORDER_ALREADY_HAS_DISCOUNT: 'orderAlreadyHasDiscount',
};

const CouponForm = ({
	appliedCoupons,
	loading,
	error,
	onApply,
	onClearError,
	intl,
}) => {
	const [isOpened, setIsOpened] = useState(false);
	const [code, setCode] = useState('');
	const { formatMessage } = intl;
	const messages = defineMessages({
		addCoupon: {
			id: 'couponForm.addCoupon',
			defaultMessage: 'Tenho um cupom de desconto',
		},
		code: {
			id: 'couponForm.code',
			defaultMessage: 'Código do cupom',
		},
		apply: {
			id: 'couponForm.apply',
			defaultMessage: 'Aplicar cupom',
		},
		cancel: {
			id: 'couponForm.cancel',
			defaultMessage: 'Cancelar',
		},
		applied: {
			id: 'couponForm.applied',
			defaultMessage: 'Cupom aplicado: {coupons}',
		},
		couponNotFound: {
			id: 'couponForm.couponNotFound',
			defaultMessage: 'Cupom não encontrado. Verifique o código e tente novamente.',
		},
		couponAlreadyUsed: {
			id: 'couponForm.couponAlreadyUsed',
			defaultMessage: 'Este cupom já foi utilizado.',
		},
		couponAlreadyAdded: {
			id: 'couponForm.couponAlreadyAdded',
			defaultMessage: 'Este cupom já está aplicado neste pedido.',
		},
		couponNotAllowed: {
			id: 'couponForm.couponNotAllowed',
			defaultMessage: 'Este cupom não é válido para este pedido.',
		},
		orderAlreadyHasDiscount: {
			id: 'couponForm.orderAlreadyHasDiscount',
			defaultMessage: 'Este pedido já possui um desconto aplicado.',
		},
		genericError: {
			id: 'couponForm.genericError',
			defaultMessage: 'Não foi possível aplicar o cupom. Tente novamente.',
		},
	});
	const trimmedCode = code.trim();
	const appliedCount = appliedCoupons.length;

	// Collapse back to the discreet link once a coupon lands on the order.
	useEffect(() => {
		setCode('');
		setIsOpened(false);
	}, [appliedCount]);

	const open = () => {
		onClearError();
		setIsOpened(true);
	};

	const close = () => {
		onClearError();
		setCode('');
		setIsOpened(false);
	};

	const submit = (e) => {
		e.preventDefault();

		if (!trimmedCode || loading) return;

		onApply(trimmedCode);
	};

	const getErrorMessage = () => {
		const messageId = ERROR_MESSAGE_IDS[error];

		return formatMessage(messageId ? messages[messageId] : messages.genericError);
	};

	return (
		<div className="CouponForm">
			{appliedCount > 0 && (
				<Text className="CouponForm__applied" variant="caption">
					{formatMessage(messages.applied, { coupons: appliedCoupons.join(', ') })}
				</Text>
			)}
			{!isOpened ? (
				<Button
					className="CouponForm__toggle"
					color="secondary"
					isLink
					size="medium"
					onClick={open}
				>
					{formatMessage(messages.addCoupon)}
				</Button>
			) : (
				<Form className="CouponForm__form" id="coupon_form" onSubmit={submit}>
					<Input
						id="coupon_code"
						className="CouponForm__input"
						type="text"
						label={formatMessage(messages.code)}
						value={code}
						disabled={loading}
						error={Boolean(error)}
						onChange={(e) => setCode(e.target.value.toUpperCase())}
					/>
					{error && (
						<Text className="CouponForm__error" variant="caption">
							{getErrorMessage()}
						</Text>
					)}
					<Button
						className="CouponForm__apply"
						color="tertiary"
						loading={loading}
						size="medium"
						type="submit"
						disabled={!trimmedCode}
					>
						{formatMessage(messages.apply)}
					</Button>
					<Button
						className="CouponForm__cancel"
						color="secondary"
						isLink
						size="medium"
						onClick={close}
					>
						{formatMessage(messages.cancel)}
					</Button>
				</Form>
			)}
		</div>
	);
};

CouponForm.propTypes = {
	appliedCoupons: PropTypes.arrayOf(PropTypes.string),
	loading: PropTypes.bool,
	error: PropTypes.string,
	onApply: PropTypes.func,
	onClearError: PropTypes.func,
	intl: PropTypes.shape().isRequired,
};

CouponForm.defaultProps = {
	appliedCoupons: [],
	loading: false,
	error: '',
	onApply: () => {},
	onClearError: () => {},
};

export default injectIntl(CouponForm);
